
import { getFirestore, collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { AdminPopupService } from "../services/admin-popup.service.js";

const db = getFirestore(app);

export const MainPopupManager = {
    init: async (container) => {
        // Find active popups
        const today = new Date().toISOString().split('T')[0];

        try {
            // Optimized Query: Remove orderBy to avoid index requirement
            const q = query(
                collection(db, 'popups'),
                where('isActive', '==', true)
            );

            const snapshot = await getDocs(q);
            if (snapshot.empty) return;

            const validPopups = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(p => {
                    return (!p.startDate || p.startDate <= today) &&
                        (!p.endDate || p.endDate >= today);
                })
                .sort((a, b) => {
                    // Sort descending by createdAt
                    const tA = a.createdAt?.seconds || 0;
                    const tB = b.createdAt?.seconds || 0;
                    return tB - tA;
                });

            if (validPopups.length === 0) return;

            // Check LocalStorage & Filter
            // Logic: Show ALL valid popups that aren't hidden.
            // Stack them so the Newest is on TOP.
            // DOM Order: Last appended is on top.
            // Data Order: Newest -> Oldest.
            // So we should append Oldest -> Newest.

            const popupsToShow = validPopups.filter(p => {
                const hideUntil = localStorage.getItem(`popup_hide_${p.id}`);
                if (!hideUntil) return true;
                return new Date().getTime() > parseInt(hideUntil);
            });

            if (popupsToShow.length > 0) {
                // Reverse to append Oldest first, so Newest is last (on top)
                popupsToShow.slice().reverse().forEach(popup => {
                    MainPopupManager.render(popup, container);
                });
            }

        } catch (e) {
            console.error("Popup Init Failed:", e);
        }
    },

    render: (originalPopup, parent) => {
        // [FIX] Resolve Translation (v1.36.251)
        // This ensures that if a translation exists for the current user locale, it is used.
        const popup = AdminPopupService.resolveTranslation(originalPopup);

        // [FIX] Prevent Duplicates (White Veil Glitch)
        const overlayId = `popup-overlay-${popup.id}`;
        if (document.getElementById(overlayId)) return;

        // Create Overlay
        const overlay = document.createElement('div');
        overlay.id = overlayId;
        overlay.className = "fixed inset-0 z-[100] flex items-center justify-center bg-black/60 fade-in p-4";

        const content = document.createElement('div');
        content.className = "bg-white rounded-xl overflow-hidden shadow-2xl max-w-sm w-[90%] animate-slide-up relative flex flex-col max-h-[85vh]";

        // Determine Link Properties
        let linkHtml = '';
        let url = popup.linkUrl ? popup.linkUrl.trim() : '';
        let isLink = url.length > 0;
        let targetAttr = '';
        let hrefValue = 'javascript:void(0)'; // Default safe value

        if (isLink) {
            // URL Normalization
            if (url.startsWith('http') || url.startsWith('//')) {
                // External
                hrefValue = url;
                targetAttr = 'target="_blank" rel="noopener noreferrer"';
            } else if (url.startsWith('www.') || /\.(com|net|org|kr|co)/i.test(url)) {
                // Loose External
                hrefValue = 'https://' + url;
                targetAttr = 'target="_blank" rel="noopener noreferrer"';
            } else {
                // Internal
                if (!url.startsWith('/')) url = '/' + url;

                // For internal SPA routing, we technically want to use router, 
                // but to ensure mobile compat, we can use href. 
                // However, to prevent full reload, we can intercept.
                // But user wants RELIABILITY functionality first.
                // Let's use hash routing fallback if using hash router, otherwise normal path.
                // Since we use a custom router, let's try to be smart.
                // Safest for mobile: Let it handle clicks naturally, but intercept for Router if possible.
                hrefValue = url;
            }
        }

        // Image template - Responsive Height
        const imgTemplate = `<img src="${popup.imageUrl}" class="w-full h-auto block" id="popup-img" style="max-height: 75vh; object-fit: contain;">`;

        const mainContent = isLink
            ? `<a href="${hrefValue}" ${targetAttr} id="popup-link" class="block w-full h-auto cursor-pointer relative bg-gray-100 font-[0]">${imgTemplate}</a>`
            : `<div class="relative w-full h-auto bg-gray-100 font-[0]">${imgTemplate}</div>`;

        // [FIX] Manual Toggle Layout - Bypassing <label> quirks
        const toggleId = `toggle-area-${popup.id}`;
        const chkId = `chk-no-show-${popup.id}`;
        const closeId = `btn-close-${popup.id}`;

        content.innerHTML = `
            <div class="overflow-y-auto overflow-x-hidden flex-1 cosmic-scrollbar">
                ${mainContent}
            </div>
            <div class="p-3 bg-gray-50 flex justify-between items-center text-sm text-gray-600 border-t shrink-0">
                <div id="${toggleId}" class="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-100 select-none active:bg-gray-200 transition-colors">
                    <input type="checkbox" id="${chkId}" class="rounded border-gray-400 text-blue-600 focus:ring-transparent w-5 h-5 pointer-events-none">
                    <span class="text-xs font-bold text-gray-700">오늘 하루 안 보기</span>
                </div>
                <button id="${closeId}" type="button" class="font-bold text-gray-800 hover:text-black px-6 py-3 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-lg transition-colors text-xs whitespace-nowrap">닫기</button>
            </div>
        `;

        overlay.appendChild(content);
        parent.appendChild(overlay);

        // Handlers
        const close = () => {
            const chk = content.querySelector(`#${chkId}`);
            const isNoShow = chk ? chk.checked : false;

            if (isNoShow) {
                const expiry = new Date().getTime() + (24 * 60 * 60 * 1000);
                localStorage.setItem(`popup_hide_${popup.id}`, expiry);
            }
            overlay.remove();
        };

        // [FIX] Manual Toggle Handler
        // We listen on the DIV. Input has pointer-events-none.
        content.querySelector(`#${toggleId}`).onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const chk = content.querySelector(`#${chkId}`);
            if (chk) chk.checked = !chk.checked;
        };

        // [FIX] Close Handler
        content.querySelector(`#${closeId}`).onclick = (e) => {
            e.stopPropagation();
            close();
        };

        // Link Handler (If exists)
        if (isLink) {
            const linkEl = content.querySelector('#popup-link');
            linkEl.onclick = (e) => {
                // Only intercept INTERNAL links for SPA router
                if (!targetAttr && window.router) {
                    e.preventDefault();
                    window.router.navigate(hrefValue);
                }

                // For external links, let browser handle it (target=_blank)

                // ALWAYS close popup after a short delay to allow nav to trigger
                setTimeout(close, 200);
            };
        }
    }
};
